/**
 * VisualThumbnail — a compact, non-interactive live preview of a saved
 * visual, used by the Visual Library grid so cards show real data instead
 * of a generic viz icon. Fetches the visual's query once on mount, caches
 * the result in a module-level Map so tab switches / re-scrolls don't
 * re-query.
 */
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';
import { BarChart3, LineChart as LineIcon, PieChart as PieIcon, Table } from 'lucide-react';

const COLORS = ['#3b82f6', '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

// Simple module-level LRU-ish cache keyed by visual_id. Values are the
// query result objects. Kept small because the library page is short.
const _cache = new Map();

function fallbackIcon(vizType) {
  if (vizType === 'line_chart' || vizType === 'area_chart') return LineIcon;
  if (vizType === 'pie_chart') return PieIcon;
  if (vizType === 'table' || vizType === 'pivot') return Table;
  return BarChart3;
}

export function VisualThumbnail({ visual }) {
  const [data, setData] = useState(_cache.get(visual.visual_id) || null);
  const [status, setStatus] = useState(data ? 'ready' : 'loading');
  const [justRefreshed, setJustRefreshed] = useState(false);

  useEffect(() => {
    if (_cache.has(visual.visual_id)) {
      setData(_cache.get(visual.visual_id));
      setStatus('ready');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (!visual.model_id) { setStatus('empty'); return; }
        const dims = [...(visual.row_dims || [])];
        if (visual.col_dim) dims.push(visual.col_dim);
        const measures = visual.value_measures || [];
        if (!dims.length && !measures.length) { setStatus('empty'); return; }
        const res = await api.executeQuery({
          model_id: visual.model_id,
          dimensions: dims,
          measures,
          limit: 30,
        });
        if (cancelled) return;
        const rows = res?.data || [];
        _cache.set(visual.visual_id, rows);
        setData(rows);
        setStatus(rows.length > 0 ? 'ready' : 'empty');
        if (rows.length > 0) {
          setJustRefreshed(true);
          const t = window.setTimeout(() => setJustRefreshed(false), 2200);
          return () => window.clearTimeout(t);
        }
      } catch (e) {
        if (!cancelled) setStatus('empty');
      }
    })();
    return () => { cancelled = true; };
  }, [visual.visual_id, visual.model_id, visual.row_dims, visual.col_dim, visual.value_measures]);

  const refreshedBadge = justRefreshed ? (
    <div
      className="absolute top-1.5 left-1.5 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[8px] font-medium uppercase tracking-wider pointer-events-none animate-in fade-in slide-in-from-top-1 duration-300"
      data-testid={`visual-thumb-refreshed-${visual.visual_id}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      Refreshed
    </div>
  ) : null;

  // Prefer a saved thumbnail image when the editor captured one.
  if (visual.thumbnail) {
    return <img src={visual.thumbnail} alt="" className="w-full h-full object-cover" data-testid={`visual-thumb-image-${visual.visual_id}`} />;
  }

  const Icon = fallbackIcon(visual.viz_type);

  if (status === 'loading') {
    return (
      <div className="w-full h-full flex items-center justify-center" data-testid={`visual-thumb-loading-${visual.visual_id}`}>
        <div className="w-6 h-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (status === 'empty' || !data || data.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center" data-testid={`visual-thumb-empty-${visual.visual_id}`}>
        <Icon className="w-10 h-10 text-muted-foreground/20" />
      </div>
    );
  }

  const rowDim = (visual.row_dims && visual.row_dims[0]) || null;
  const measures = visual.value_measures || [];
  const measure = measures[0] || Object.keys(data[0]).find((k) => typeof data[0][k] === 'number');
  const chartData = data.slice(0, 20);

  const commonProps = {
    data: chartData,
    margin: { top: 4, right: 6, bottom: 4, left: 6 },
  };

  const wrap = (child) => (
    <div className="relative w-full h-full p-2" data-testid={`visual-thumb-chart-${visual.visual_id}`}>
      {refreshedBadge}
      <ResponsiveContainer width="100%" height="100%">{child}</ResponsiveContainer>
    </div>
  );

  if (visual.viz_type === 'line_chart' || visual.viz_type === 'combo') {
    return wrap(
      <LineChart {...commonProps}>
        {measures.length ? measures.map((m, i) => (
          <Line key={m} type="monotone" dataKey={m} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} isAnimationActive={false} />
        )) : (
          <Line type="monotone" dataKey={measure} stroke={COLORS[0]} strokeWidth={2} dot={false} isAnimationActive={false} />
        )}
      </LineChart>
    );
  }
  if (visual.viz_type === 'area_chart') {
    return wrap(
      <AreaChart {...commonProps}>
        {(measures.length ? measures : [measure]).map((m, i) => (
          <Area key={m} type="monotone" dataKey={m} stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.25} strokeWidth={2} isAnimationActive={false} />
        ))}
      </AreaChart>
    );
  }
  if (visual.viz_type === 'pie_chart' || visual.viz_type === 'funnel') {
    const key = measure || 'value';
    return wrap(
      <PieChart>
        <Pie data={chartData} dataKey={key} nameKey={rowDim || Object.keys(chartData[0])[0]} innerRadius="40%" outerRadius="80%" stroke="none" isAnimationActive={false}>
          {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
      </PieChart>
    );
  }
  if (visual.viz_type === 'table' || visual.viz_type === 'pivot') {
    const keys = Object.keys(chartData[0] || {}).slice(0, 3);
    return (
      <div className="relative w-full h-full overflow-hidden p-2" data-testid={`visual-thumb-table-${visual.visual_id}`}>
        {refreshedBadge}
        <table className="w-full text-[8px] font-mono">
          <thead>
            <tr className="border-b border-border/40">
              {keys.map((k) => <th key={k} className="text-left px-1 py-0.5 text-muted-foreground truncate">{k}</th>)}
            </tr>
          </thead>
          <tbody>
            {chartData.slice(0, 6).map((row, i) => (
              <tr key={i} className="border-b border-border/10">
                {keys.map((k) => (
                  <td key={k} className="px-1 py-0.5 truncate max-w-[60px]">{typeof row[k] === 'number' ? row[k].toLocaleString(undefined, { maximumFractionDigits: 1 }) : String(row[k] ?? '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // default: bar chart
  return wrap(
    <BarChart {...commonProps}>
      {(measures.length ? measures : [measure]).map((m, i) => (
        <Bar key={m} dataKey={m} fill={COLORS[i % COLORS.length]} radius={[2, 2, 0, 0]} isAnimationActive={false} />
      ))}
    </BarChart>
  );
}

export default VisualThumbnail;
